import { OverlayLayerContext, useOverlayLayer } from "./OverlayLayer";
import { Menu as BaseMenu } from "@base-ui/react/menu";
import { Check } from "lucide-react";
import type { ComponentProps } from "react";
import { cn } from "../lib/cn";
import { formatShortcut, type Platform } from "../lib/shortcut";
import { popupClassName } from "./Popover";

const Root = BaseMenu.Root;
const Trigger = BaseMenu.Trigger;
const Group = BaseMenu.Group;

export interface DropdownMenuContentProps extends Omit<BaseMenu.Popup.Props, "className"> {
  className?: string;
  side?: BaseMenu.Positioner.Props["side"];
  align?: BaseMenu.Positioner.Props["align"];
  sideOffset?: number;
  alignOffset?: number;
}

function Content({
  className,
  side = "bottom",
  align = "start",
  sideOffset = 4,
  alignOffset = 0,
  ...props
}: DropdownMenuContentProps) {
  const layer = useOverlayLayer("dropdown");
  return (
    <OverlayLayerContext value={layer}>
      <BaseMenu.Portal>
        <BaseMenu.Positioner
          style={{ zIndex: layer }}
          side={side}
          align={align}
          sideOffset={sideOffset}
          alignOffset={alignOffset}
          className="z-dropdown outline-none"
        >
          <BaseMenu.Popup
            data-slot="dropdown-menu-content"
            className={cn(popupClassName, "min-w-40 p-1", className)}
            {...props}
          />
        </BaseMenu.Positioner>
      </BaseMenu.Portal>
    </OverlayLayerContext>
  );
}

export const menuItemClassName =
  "relative flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-fg outline-none select-none data-highlighted:bg-fg/6 data-disabled:pointer-events-none data-disabled:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-fg-muted";

export interface DropdownMenuItemProps extends Omit<BaseMenu.Item.Props, "className"> {
  className?: string;
  variant?: "default" | "destructive";
}

function Item({ className, variant = "default", ...props }: DropdownMenuItemProps) {
  return (
    <BaseMenu.Item
      data-slot="dropdown-menu-item"
      data-variant={variant}
      className={cn(
        menuItemClassName,
        variant === "destructive" &&
          "text-danger data-highlighted:bg-danger/10 data-highlighted:text-danger [&_svg]:text-danger",
        className,
      )}
      {...props}
    />
  );
}

export interface DropdownMenuCheckboxItemProps extends Omit<
  BaseMenu.CheckboxItem.Props,
  "className"
> {
  className?: string;
}

function CheckboxItem({ className, children, ...props }: DropdownMenuCheckboxItemProps) {
  return (
    <BaseMenu.CheckboxItem
      data-slot="dropdown-menu-checkbox-item"
      className={cn(menuItemClassName, "pl-7", className)}
      {...props}
    >
      <BaseMenu.CheckboxItemIndicator className="absolute left-2 flex items-center justify-center">
        <Check className="size-3.5 text-fg" strokeWidth={2.5} />
      </BaseMenu.CheckboxItemIndicator>
      {children}
    </BaseMenu.CheckboxItem>
  );
}

function Separator({ className, ...props }: BaseMenu.Separator.Props & { className?: string }) {
  return (
    <BaseMenu.Separator
      data-slot="dropdown-menu-separator"
      className={cn("-mx-1 my-1 h-px bg-border", className)}
      {...props}
    />
  );
}

function Label({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="dropdown-menu-label"
      className={cn("px-2 py-1.5 text-xs font-medium text-fg-muted", className)}
      {...props}
    />
  );
}

export interface DropdownMenuShortcutProps extends Omit<ComponentProps<"span">, "children"> {
  /** A combo like `"mod+k"`, formatted for the platform. */
  keys: string;
  platform?: Platform;
}

function Shortcut({ keys, platform, className, ...props }: DropdownMenuShortcutProps) {
  return (
    <span
      data-slot="dropdown-menu-shortcut"
      className={cn("ml-auto pl-4 text-xs tracking-wide text-fg-faint", className)}
      {...props}
    >
      {platform === undefined ? formatShortcut(keys) : formatShortcut(keys, platform)}
    </span>
  );
}

/**
 * Anatomy: `DropdownMenu.Root > DropdownMenu.Trigger + DropdownMenu.Content > (Label | Item | CheckboxItem | Separator | Group)*`.
 * `Item` accepts `onClick`; `CheckboxItem` accepts `checked` / `onCheckedChange`.
 */
export const DropdownMenu = {
  Root,
  Trigger,
  Content,
  Item,
  CheckboxItem,
  Separator,
  Label,
  Shortcut,
  Group,
};
